import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot, PageHeader, KpiGrid, KpiCard, Toolbar } from '../ui/Page';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { Notice, StatusBadge } from '../ui/Badge';
// ⭐ The request vocabulary moved to `status.ts` when the dispatch board's triage queue
// became a second reader (114 part 5) — one vocabulary per entity, §6.2, and the closed-set
// typing that makes a new state a build error moved with it.
import { requestStatus as statusOf } from '../status';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { LogCallSheet } from '../create/LogCallSheet';
import { ProjectLinkCard } from '../projects/ProjectLinkCard';
import { ServiceTypeStep } from '../servicetypes/ServiceTypeStep';
import { refetchServiceTypes } from '../servicetypes/ServiceTypePicker';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import {
  LineItemBuilder,
  emptyLine,
  parseLines,
  type LineDraft,
} from '../money/LineItemBuilder';
import { IconRequests, IconPlus } from '../icons';
import { useOnline } from '../ui/useConnection';

type Request = {
  id: string;
  requestNumber: number;
  status: string;
  source: string;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  addressText: string | null;
  title: string;
  details: string | null;
  urgency: string;
  preferredWindow: string | null;
  declineReason: string | null;
  convertedJobId: string | null;
  convertedQuoteId: string | null;
  convertedCommercialJobId?: string | null;
  /** Optional everywhere — a residential shop never sets one (PRD v2 §6.8). */
  projectId: string | null;
  createdAt: string | Date;
};

const TABS = [
  { key: 'new', label: 'Needs triage' },
  { key: 'done', label: 'Handled' },
];

/** What the triage sheet shows: its menu, or the step one of the menu's buttons opened. */
type Mode = 'menu' | 'job' | 'quote' | 'decline';

/**
 * The validation gate (PRD v2 §4). Raw intake is NOT work: every request is approved
 * into a quote or a job, or declined, before anything reaches the pipeline. Triage is
 * also the moment a stranger becomes a customer — the server does that atomically.
 */
export function RequestsPage() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState('new');
  const { search, setSearch, query, table } = useServerTable();
  // The tab is part of the query, not a filter over what came back.
  const requests = useQuery({
    ...trpc.request.list.queryOptions({ ...query, triaged: tab !== 'new' }),
    placeholderData: (prev) => prev,
  });
  const counts = useQuery(trpc.request.counts.queryOptions());
  const workflow = useQuery(trpc.jobPipeline.list.queryOptions());
  const online = useOnline();
  const workflowKnown =
    online && workflow.data !== undefined && !workflow.isError && !workflow.isPaused;
  const commercialWorkflow = workflow.data?.enabled === true;
  const [logging, setLogging] = useState(false);
  const [triage, setTriage] = useState<Request | null>(null);
  const [mode, setMode] = useState<Mode>('menu');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [reason, setReason] = useState('');
  // "Approve → Job" opens a step that asks for the new job's service type
  // (`ServiceTypeStep`, ops insights ticket 05).
  const [serviceTypeId, setServiceTypeId] = useState('');
  /** The menu's buttons, by the mode each opens: Back hands focus to the one it came from. */
  const openers = useRef<Partial<Record<Mode, HTMLButtonElement | null>>>({});
  const openerFor = (opens: Mode) => (el: HTMLButtonElement | null) => {
    openers.current[opens] = el;
  };

  /**
   * The menu again, out of whatever mode the sheet is in, forgetting the type picked in the job
   * mode: another request, or this one decided some other way, must be asked again rather than
   * inherit it.
   */
  const showMenu = () => {
    setMode('menu');
    setServiceTypeId('');
  };

  const done = () => {
    void qc.invalidateQueries();
    setTriage(null);
    showMenu();
    setLines([emptyLine()]);
    setReason('');
  };

  const toJob = useMutation(
    trpc.request.approveToJob.mutationOptions({
      onSuccess: done,
      onError: () => refetchServiceTypes(qc, trpc),
    }),
  );
  const toCommercialJob = useMutation(
    trpc.request.approveToCommercialJob.mutationOptions({
      onSuccess: (job) => {
        done();
        void navigate({ to: '/jobs/$jobId', params: { jobId: job.id } });
      },
    }),
  );

  /**
   * Every mode's Back. It also clears the job mode's refusal, so the menu, and whatever the
   * sheet shows next, never draws a refusal for a step no longer on screen.
   *
   * Back goes away with the mode it was pressed in, so focus is handed to the menu button that
   * opened that mode, rather than falling out of the sheet. `flushSync` draws the menu first, so
   * that button is there to take it.
   */
  const backToMenu = () => {
    const from = mode;
    toJob.reset();
    flushSync(showMenu);
    openers.current[from]?.focus();
  };

  const toQuote = useMutation(
    trpc.request.approveToQuote.mutationOptions({ onSuccess: done }),
  );
  const decline = useMutation(trpc.request.decline.mutationOptions({ onSuccess: done }));
  const reopen = useMutation(trpc.request.reopen.mutationOptions({ onSuccess: done }));
  const busy =
    toJob.isPending ||
    toCommercialJob.isPending ||
    toQuote.isPending ||
    decline.isPending ||
    reopen.isPending;
  // Not `toJob`'s: the job mode draws its refusal in words (a type turned off is not something
  // to "try again"), and every way out of the job mode clears it.
  const failed = toQuote.isError || decline.isError || reopen.isError;

  if (requests.isPending) return <PageSkeleton />;
  if (requests.isError) {
    return (
      <PageRoot>
        <PageHeader title="Requests" />
        <ErrorState
          icon={IconRequests}
          what="requests"
          onRetry={() => void requests.refetch()}
        />
      </PageRoot>
    );
  }

  const rows = requests.data.rows as Request[];

  const columns: DataTableColumn<Request>[] = [
    {
      // Keyed to REQUEST_SORT: sorts by request number, the same convention as Jobs.
      // Its old key `what` sorted by title — into an allow-list that silently dropped
      // it, so the header cycled aria-sort over an order that never changed.
      key: 'request',
      label: 'Request',
      value: (r) => r.requestNumber,
      searchValue: (r) => r.title,
      cell: (r) => (
        <div>
          <p className="font-medium">{r.title}</p>
          <p className="font-mono text-xs text-muted-foreground">#{r.requestNumber}</p>
        </div>
      ),
    },
    {
      key: 'who',
      label: 'From',
      value: (r) => r.contactName,
      cell: (r) => (
        <div>
          <p>{r.contactName}</p>
          <p className="text-xs text-muted-foreground">
            {dash(r.contactPhone ?? r.contactEmail)}
          </p>
        </div>
      ),
    },
    {
      key: 'urgency',
      label: 'Urgency',
      value: (r) => r.urgency,
      cell: (r) =>
        r.urgency === 'emergency' ? (
          <StatusBadge tone="danger">Emergency</StatusBadge>
        ) : r.urgency === 'low' ? (
          <span className="text-muted-foreground">Low</span>
        ) : (
          <span className="text-muted-foreground">Normal</span>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      value: (r) => statusOf(r.status).label,
      cell: (r) => {
        const s = statusOf(r.status);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'source',
      label: 'Came from',
      value: (r) => r.source,
      cell: (r) => dash(r.source.replace(/_/g, ' ')),
    },
    {
      // `created`, matching REQUEST_SORT — its old key `when` was another silent no-op.
      key: 'created',
      label: 'Received',
      value: (r) => new Date(r.createdAt).getTime(),
      searchValue: (r) => new Date(r.createdAt).toLocaleDateString(),
      cell: (r) => new Date(r.createdAt).toLocaleDateString(),
      align: 'right',
    },
  ];

  const parsedLines = parseLines(lines);

  return (
    <PageRoot>
      <PageHeader
        title="Requests"
        sub="Nothing reaches the pipeline until you approve it here."
        right={
          <Button onClick={() => setLogging(true)}>
            <IconPlus size={15} />
            Log a call
          </Button>
        }
      />
      <KpiGrid>
        {/* Counted in SQL over every request, not over the page on screen. `—` while it
            loads, never a 0 that reads as "nothing is waiting on you". */}
        <KpiCard
          label="Needs triage"
          value={counts.data?.untriaged ?? '—'}
          tone="brand"
        />
        <KpiCard
          label="Emergencies"
          value={counts.data?.emergencies ?? '—'}
          tone={counts.data && counts.data.emergencies > 0 ? 'danger' : undefined}
        />
        <KpiCard
          label="Turned into work"
          value={counts.data?.turnedIntoWork ?? '—'}
          tone="success"
        />
        <KpiCard label="Declined" value={counts.data?.declined ?? '—'} />
      </KpiGrid>

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip tabs={TABS} />
      </UnderlineTabs>

      <Toolbar>
        <SearchInput
          placeholder="Search requests…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search requests"
        />
      </Toolbar>

      <DataTable
        tableId="requests"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        search={search}
        server={table(requests.data.total, requests.isFetching)}
        onRowClick={(r) => {
          setTriage(r);
          setMode('menu');
        }}
        rowLabel={(r) => `Triage ${r.title}`}
        empty={
          <EmptyState
            icon={IconRequests}
            title={tab === 'new' ? 'Nothing waiting on you' : 'Nothing handled yet'}
            description={
              tab === 'new'
                ? 'New requests you log from calls and emails land here first.'
                : 'Requests you approve or decline move here, with what they became.'
            }
          />
        }
      />

      <Sheet
        // Sonar-format width: the approve-to-quote mode embeds a line-item builder,
        // and the width must not jump when the user picks that path (104).
        wide
        open={triage !== null}
        onClose={() => {
          setTriage(null);
          showMenu();
        }}
        title={triage?.title ?? ''}
        badge={
          triage ? (
            <StatusBadge tone={statusOf(triage.status).tone}>
              {statusOf(triage.status).label}
            </StatusBadge>
          ) : null
        }
      >
        {triage ? (
          <div className="space-y-4 text-sm">
            <dl className="space-y-2">
              {(
                [
                  ['From', triage.contactName],
                  ['Phone', triage.contactPhone],
                  ['Email', triage.contactEmail],
                  ['Address', triage.addressText],
                  ['Prefers', triage.preferredWindow],
                  ['Came from', triage.source.replace(/_/g, ' ')],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-4">
                  <dt className="shrink-0 text-muted-foreground">{k}</dt>
                  <dd className="text-right font-medium">{dash(v)}</dd>
                </div>
              ))}
            </dl>

            {triage.details ? (
              <p className="whitespace-pre-wrap rounded-[var(--radius-sm)] bg-surface-2 p-3">
                {triage.details}
              </p>
            ) : null}

            {/*
              No customerId: a request is a stranger until triage links or creates one, so
              every open project is a legitimate destination. Tagging it HERE is the point —
              a call about a building already under contract should join that build before
              it becomes a quote, not after somebody notices.
            */}
            <ProjectLinkCard
              entityType="request"
              entityId={triage.id}
              projectId={triage.projectId}
            />

            {triage.status !== 'new' ? (
              <div className="border-t border-border pt-4">
                <p className="text-muted-foreground">
                  {triage.status === 'declined'
                    ? `Declined — ${triage.declineReason ?? 'no reason recorded'}`
                    : triage.status === 'archived'
                      ? 'Archived — nobody got to this one in time.'
                      : 'Already approved. It lives in the pipeline now.'}
                </p>
                {/*
                  The sweep archives on a timer with no judgement, so the way back has to be
                  on the screen. Without this the automation is a one-way door: the customer
                  rings three months later and there is nothing to click.
                */}
                {triage.status === 'archived' ? (
                  <Button
                    className="mt-2"
                    disabled={busy}
                    onClick={() => reopen.mutate({ requestId: triage.id, ...idem() })}
                  >
                    Put it back on the list
                  </Button>
                ) : null}
                {triage.convertedJobId ? (
                  <Button
                    variant="ghost"
                    className="mt-2"
                    onClick={() =>
                      navigate({
                        to: '/jobs/$jobId',
                        params: { jobId: triage.convertedJobId! },
                      })
                    }
                  >
                    Open the job
                  </Button>
                ) : null}
                {triage.convertedCommercialJobId ? (
                  <Button
                    variant="ghost"
                    className="mt-2"
                    onClick={() =>
                      navigate({
                        to: '/jobs/$jobId',
                        params: { jobId: triage.convertedCommercialJobId! },
                      })
                    }
                  >
                    Open the job
                  </Button>
                ) : null}
                {triage.convertedQuoteId ? (
                  <Button
                    variant="ghost"
                    className="mt-2"
                    onClick={() =>
                      navigate({
                        to: '/quotes/$quoteId',
                        params: { quoteId: triage.convertedQuoteId! },
                      })
                    }
                  >
                    Open the quote
                  </Button>
                ) : null}
              </div>
            ) : mode === 'menu' ? (
              <div className="space-y-2 border-t border-border pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Decide
                </p>
                {!workflowKnown ? (
                  <Notice tone="warn">
                    {workflow.isPaused
                      ? 'Reconnect to confirm the workflow before converting this request.'
                      : workflow.isError
                        ? 'Workflow settings could not refresh. Try again before converting this request.'
                        : 'Checking workflow settings…'}
                    {workflow.isError ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void workflow.refetch()}
                      >
                        Try again
                      </Button>
                    ) : null}
                  </Notice>
                ) : null}
                {toCommercialJob.error ? (
                  <Notice tone="danger" alert>
                    {toCommercialJob.error.message}
                  </Notice>
                ) : null}
                {commercialWorkflow ? (
                  <Button
                    className="w-full"
                    disabled={busy || !workflowKnown}
                    onClick={() =>
                      toCommercialJob.mutate({ requestId: triage.id, ...idem() })
                    }
                  >
                    Convert to job
                  </Button>
                ) : (
                  <Button
                    ref={openerFor('job')}
                    className="w-full"
                    disabled={busy || !workflowKnown}
                    onClick={() => {
                      // A refusal from an earlier try (maybe another request's) is not this
                      // one's: the job mode starts without it.
                      toJob.reset();
                      setMode('job');
                    }}
                  >
                    Approve → Job
                  </Button>
                )}
                {!commercialWorkflow ? (
                  <Button
                    ref={openerFor('quote')}
                    variant="ghost"
                    className="w-full"
                    disabled={busy || !workflowKnown}
                    onClick={() => setMode('quote')}
                  >
                    Approve → Quote
                  </Button>
                ) : null}
                <Button
                  ref={openerFor('decline')}
                  variant="ghost"
                  className="w-full"
                  disabled={busy}
                  onClick={() => setMode('decline')}
                >
                  Decline
                </Button>
                <p className="pt-1 text-xs text-muted-foreground">
                  {commercialWorkflow
                    ? 'The request stays linked to the Job. Add its quotes there; confirmation and scheduling are separate.'
                    : 'Approving creates the customer and the service address for you.'}
                </p>
              </div>
            ) : mode === 'job' ? (
              // The job is created only once its service type is picked (`ServiceTypeStep`).
              <div className="space-y-3 border-t border-border pt-4">
                {/* The refusal, once, in words, and announced (`alert`): the step keeps
                    focus, so a screen reader would not otherwise hear it. */}
                {toJob.error ? (
                  <Notice tone="danger" alert>
                    {toJob.error.message}
                  </Notice>
                ) : null}
                <ServiceTypeStep
                  value={serviceTypeId}
                  onChange={setServiceTypeId}
                  pending={toJob.isPending}
                  // Any other triage write in flight. None can be from here (every way into
                  // this mode is off while one is), but the step does not rely on that.
                  locked={busy}
                  cancelLabel="Back"
                  onSubmit={(picked) =>
                    toJob.mutate({
                      requestId: triage.id,
                      serviceTypeId: picked,
                      ...idem(),
                    })
                  }
                  onCancel={backToMenu}
                />
              </div>
            ) : mode === 'quote' ? (
              <div className="space-y-3 border-t border-border pt-4">
                <LineItemBuilder
                  lines={lines}
                  onChange={setLines}
                  label="What are you quoting?"
                />
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    disabled={busy || parsedLines.length === 0}
                    onClick={() =>
                      toQuote.mutate({
                        requestId: triage.id,
                        lines: parsedLines,
                        ...idem(),
                      })
                    }
                  >
                    Create quote
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={backToMenu}>
                    Back
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 border-t border-border pt-4">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Why are you declining?
                  </span>
                  <input
                    className="mt-1 h-10 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                    placeholder="Outside our service area"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    disabled={busy}
                    onClick={() =>
                      decline.mutate({
                        requestId: triage.id,
                        reason: reason || undefined,
                        ...idem(),
                      })
                    }
                  >
                    Decline request
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={backToMenu}>
                    Back
                  </Button>
                </div>
              </div>
            )}

            {failed ? (
              <p className="text-xs text-[var(--sw-danger-text)]">
                That didn't go through. Nothing was changed — try again.
              </p>
            ) : null}
          </div>
        ) : null}
      </Sheet>
      <LogCallSheet open={logging} onClose={() => setLogging(false)} />
    </PageRoot>
  );
}
